import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_DYNAMIC_PROP,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_AI_VEHICLE,
  COLLISION_GROUP_GROUND,
} from '../core/PhysicsWorld';
import { RoadNetwork } from './RoadNetwork';
import { Roundabout } from './Roundabout';
import { ParkingZones } from './ParkingZones';
import { TrafficLight } from './TrafficLights';
import { TrafficSigns } from './TrafficSigns';

export interface DynamicProp {
  mesh: THREE.Group;
  body: CANNON.Body;
}

export class TownBuilder {
  public scene: THREE.Scene;
  public physicsWorld: PhysicsWorld;
  public roadNetwork: RoadNetwork;
  public roundabout: Roundabout;
  public parkingZones: ParkingZones;
  public trafficLights: TrafficLight[] = [];
  public dynamicProps: DynamicProp[] = [];
  public parkedCarPositions: THREE.Vector3[] = [];
  public treePositions: THREE.Vector3[] = [];

  public buildingBlocks: Array<{ x: number; z: number; w: number; d: number }> = [
    // Inner North quadrants - split into South row (z=18) and North row (z=43) to leave clear corridor for Fahrradstraße at z=30
    { x: 26, z: 18, w: 30, d: 14 },
    { x: 26, z: 43, w: 30, d: 14 },
    { x: -26, z: 18, w: 30, d: 14 },
    { x: -26, z: 43, w: 30, d: 14 },
    { x: 74, z: 18, w: 26, d: 14 },
    { x: 74, z: 43, w: 26, d: 14 },
    { x: -74, z: 18, w: 26, d: 14 },
    { x: -74, z: 43, w: 26, d: 14 },

    // Inner South quadrants (z = -26)
    { x: -26, z: -26, w: 30, d: 30 },
    { x: 26, z: -26, w: 30, d: 30 },
    { x: 74, z: -26, w: 26, d: 30 },
    { x: -74, z: -26, w: 26, d: 30 },
    // Outer district blocks
    { x: 26, z: 88, w: 30, d: 24 },
    { x: -26, z: 88, w: 30, d: 24 },
    { x: 26, z: -88, w: 30, d: 24 },
    { x: -26, z: -88, w: 30, d: 24 },
    { x: 74, z: 88, w: 26, d: 24 },
    { x: -74, z: 88, w: 26, d: 24 },
    { x: 74, z: -88, w: 26, d: 24 },
    { x: -74, z: -88, w: 26, d: 24 },
  ];

  constructor(scene: THREE.Scene, physicsWorld: PhysicsWorld) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;

    this.roadNetwork = new RoadNetwork(physicsWorld);
    this.roundabout = new Roundabout(physicsWorld);
    this.parkingZones = new ParkingZones(physicsWorld);

    this.scene.add(this.roadNetwork.group);
    this.scene.add(this.roundabout.group);
    this.scene.add(this.parkingZones.group);

    this.buildGround();
    this.buildBuildings();
    this.buildTreesAndProps();
    this.buildStreetLamps();
    this.buildTrafficLights();
    this.buildSignPosts();
    this.buildMotorwayInfrastructure();
    this.buildLeitpfosten();
    this.buildIndustrialZone();
    this.buildRoadsideParkedCars();
  }

  private buildGround() {
    // 1. Lush German green terrain base
    const groundGeom = new THREE.PlaneGeometry(1400, 1400);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x3d7a42, // Vibrant park lawn green
      roughness: 0.95,
      metalness: 0.05,
    });
    const ground = new THREE.Mesh(groundGeom, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.005;
    ground.receiveShadow = true;
    this.scene.add(ground);

    // 2. Concrete building foundations / sidewalks plazas
    const plazaGeom = new THREE.PlaneGeometry(36, 36);
    const plazaMat = new THREE.MeshStandardMaterial({ color: 0xcfd8dc, roughness: 0.9 });
    const plazaPositions = [
      { x: 26, z: 26 },
      { x: -26, z: 26 },
      { x: -26, z: -26 },
      { x: 26, z: -26 },
      { x: 74, z: 26 },
      { x: -74, z: 26 },
      { x: 74, z: -26 },
      { x: -74, z: -26 },
    ];
    plazaPositions.forEach((p) => {
      const plaza = new THREE.Mesh(plazaGeom, plazaMat);
      plaza.rotation.x = -Math.PI / 2;
      plaza.position.set(p.x, 0.002, p.z);
      plaza.receiveShadow = true;
      this.scene.add(plaza);
    });
  }

  private createFacadeTexture(baseColorHex: number, isShop: boolean): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    const hexStr = '#' + baseColorHex.toString(16).padStart(6, '0');
    ctx.fillStyle = hexStr;
    ctx.fillRect(0, 0, 512, 512);

    // Stone decorative cornices / horizontal bands
    ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.fillRect(0, 120, 512, 6);
    ctx.fillRect(0, 240, 512, 6);
    ctx.fillRect(0, 360, 512, 6);

    // Window grid
    const rows = 4;
    const cols = 5;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = 36 + c * 92;
        const y = 32 + r * 115;
        const w = 48;
        const h = 64;

        if (r === rows - 1 && isShop) {
          // Ground floor store display window & shop front
          ctx.fillStyle = '#1e293b';
          ctx.fillRect(x - 8, y - 8, w + 16, h + 24);
          ctx.fillStyle = '#0284c7';
          ctx.fillRect(x - 4, y - 4, w + 8, h + 16);
          continue;
        }

        // White window frame
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x - 3, y - 3, w + 6, h + 6);

        // Window glass with blue sky reflection
        const winGrad = ctx.createLinearGradient(x, y, x, y + h);
        winGrad.addColorStop(0, '#0f172a');
        winGrad.addColorStop(1, '#38bdf8');
        ctx.fillStyle = winGrad;
        ctx.fillRect(x, y, w, h);

        // Cross muntins
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fillRect(x + w / 2 - 1, y, 2, h);
        ctx.fillRect(x, y + h / 2 - 1, w, 2);

        // Stone window sill
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(x - 5, y + h + 2, w + 10, 4);
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 4;
    return texture;
  }


  private createApartmentTowerTexture(baseColorHex: number): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    const hexStr = '#' + baseColorHex.toString(16).padStart(6, '0');
    ctx.fillStyle = hexStr;
    ctx.fillRect(0, 0, 512, 512);

    // Modern apartment high-rise facade with vertical columns and balconies
    const floors = 8;
    for (let f = 0; f < floors; f++) {
      const y = f * (512 / floors);
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      ctx.fillRect(0, y, 512, 4);

      const cols = 4;
      for (let c = 0; c < cols; c++) {
        const x = 24 + c * 120;
        // Window
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(x, y + 8, 72, 30);

        const winGrad = ctx.createLinearGradient(x, y + 8, x, y + 38);
        winGrad.addColorStop(0, '#1e293b');
        winGrad.addColorStop(1, '#38bdf8');
        ctx.fillStyle = winGrad;
        ctx.fillRect(x + 2, y + 10, 68, 26);

        // Balcony railing
        ctx.fillStyle = '#64748b';
        ctx.fillRect(x - 4, y + 34, 80, 16);
        ctx.fillStyle = '#334155';
        ctx.fillRect(x - 4, y + 34, 80, 3);
        for (let b = 0; b < 5; b++) {
          ctx.fillRect(x + b * 16, y + 37, 2, 13);
        }
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 4;
    return texture;
  }

  private createCommercialFacadeTexture(): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    // Industrial gray ribbed metal panels with blue top trim
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(0, 0, 512, 512);

    // Blue roof / top header trim (matching reference image)
    ctx.fillStyle = '#1d4ed8';
    ctx.fillRect(0, 0, 512, 48);

    // Vertical corrugated ribs
    ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    for (let x = 0; x < 512; x += 16) {
      ctx.fillRect(x, 48, 2, 512);
    }

    // Modern horizontal window strip
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(32, 160, 448, 64);
    const winGrad = ctx.createLinearGradient(0, 160, 0, 224);
    winGrad.addColorStop(0, '#1e293b');
    winGrad.addColorStop(1, '#0284c7');
    ctx.fillStyle = winGrad;
    ctx.fillRect(36, 164, 440, 56);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 4;
    return texture;
  }

  public createRoofTileTexture(): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    // Warm terracotta clay base
    ctx.fillStyle = '#9a3412';
    ctx.fillRect(0, 0, 512, 512);

    const rows = 16;
    const cols = 16;
    const tileW = 512 / cols;
    const tileH = 512 / rows;

    for (let r = 0; r < rows; r++) {
      const y = r * tileH;
      const xOffset = (r % 2) * (tileW / 2);
      for (let c = -1; c <= cols; c++) {
        const x = c * tileW + xOffset;
        const tone = ((r * 7 + c * 13) % 5);
        const tileColors = ['#8c2e10', '#9a3412', '#a83c18', '#852b0d', '#b0431f'];
        ctx.fillStyle = tileColors[tone];

        // German Biberschwanz (rounded beaver-tail bottom)
        ctx.beginPath();
        ctx.moveTo(x + 1, y);
        ctx.lineTo(x + tileW - 1, y);
        ctx.lineTo(x + tileW - 1, y + tileH * 0.7);
        ctx.bezierCurveTo(x + tileW - 1, y + tileH, x + 1, y + tileH, x + 1, y + tileH * 0.7);
        ctx.closePath();
        ctx.fill();

        // Dark overlap shadow
        ctx.fillStyle = 'rgba(20, 5, 2, 0.45)';
        ctx.fillRect(x, y + tileH - 2, tileW, 2);

        // Highlight along top edge
        ctx.strokeStyle = 'rgba(255, 180, 140, 0.25)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 4;
    return texture;
  }

  public createFachwerkFacadeTexture(baseColorHex: number, storeName?: string): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    // Warm ivory / cream plaster
    const hexStr = '#' + baseColorHex.toString(16).padStart(6, '0');
    ctx.fillStyle = hexStr || '#fef3c7';
    ctx.fillRect(0, 0, 512, 512);

    // Light stucco noise
    ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
    for (let i = 0; i < 300; i++) {
      const px = (i * 37) % 512;
      const py = (i * 73) % 512;
      ctx.fillRect(px, py, 2, 2);
    }

    // Heavy dark timber frame beams (Aged German Oak: #26140d)
    const timberColor = '#26140d';
    const timberHighlight = '#3f2115';

    // Horizontal story beams (Schwellen und Rähme)
    const storyYs = [0, 160, 330, 500];
    for (const sy of storyYs) {
      ctx.fillStyle = timberColor;
      ctx.fillRect(0, sy, 512, 14);
      ctx.fillStyle = timberHighlight;
      ctx.fillRect(0, sy + 1, 512, 2);
    }

    // Vertical posts (Eckständer und Bundständer)
    const postXs = [0, 128, 256, 384, 498];
    for (const px of postXs) {
      ctx.fillStyle = timberColor;
      ctx.fillRect(px, 0, 14, 512);
      ctx.fillStyle = timberHighlight;
      ctx.fillRect(px + 1, 0, 2, 512);
    }

    // Diagonal timber braces (K-Verband / Andreaskreuze) in upper stories
    ctx.strokeStyle = timberColor;
    ctx.lineWidth = 10;
    // Floor 1 braces (y: 160..330)
    ctx.beginPath();
    ctx.moveTo(14, 174); ctx.lineTo(128, 330);
    ctx.moveTo(128, 174); ctx.lineTo(14, 330);
    ctx.moveTo(384, 174); ctx.lineTo(498, 330);
    ctx.moveTo(498, 174); ctx.lineTo(384, 330);
    ctx.stroke();

    // Floor 2 braces (y: 0..160)
    ctx.beginPath();
    ctx.moveTo(128, 14); ctx.lineTo(256, 160);
    ctx.moveTo(384, 14); ctx.lineTo(256, 160);
    ctx.stroke();

    // Windows in floors 1 & 2
    const windowPositions = [
      // Floor 2 (top)
      { x: 42, y: 38, w: 48, h: 72 },
      { x: 170, y: 38, w: 48, h: 72 },
      { x: 298, y: 38, w: 48, h: 72 },
      { x: 426, y: 38, w: 48, h: 72 },
      // Floor 1 (middle)
      { x: 42, y: 200, w: 48, h: 72 },
      { x: 170, y: 200, w: 48, h: 72 },
      { x: 298, y: 200, w: 48, h: 72 },
      { x: 426, y: 200, w: 48, h: 72 },
    ];

    for (const wp of windowPositions) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(wp.x - 3, wp.y - 3, wp.w + 6, wp.h + 6);

      const winGrad = ctx.createLinearGradient(wp.x, wp.y, wp.x, wp.y + wp.h);
      winGrad.addColorStop(0, '#0f172a');
      winGrad.addColorStop(0.5, '#1e293b');
      winGrad.addColorStop(1, '#60a5fa');
      ctx.fillStyle = winGrad;
      ctx.fillRect(wp.x, wp.y, wp.w, wp.h);

      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(wp.x + wp.w / 2 - 1.5, wp.y, 3, wp.h);
      ctx.fillRect(wp.x, wp.y + wp.h / 2 - 1.5, wp.w, 3);

      ctx.fillStyle = '#1c0f0a';
      ctx.fillRect(wp.x - 5, wp.y + wp.h + 2, wp.w + 10, 5);
    }

    // Ground Floor: Storefront or Timber Archway
    if (storeName) {
      ctx.fillStyle = '#1e1b18';
      ctx.fillRect(20, 348, 472, 34);
      ctx.fillStyle = '#b45309';
      ctx.fillRect(20, 348, 472, 3);
      ctx.fillRect(20, 379, 472, 3);

      ctx.fillStyle = '#fef08a';
      ctx.font = 'bold 20px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(storeName, 256, 365);

      ctx.fillStyle = '#0f172a';
      ctx.fillRect(28, 392, 180, 96);
      ctx.fillRect(304, 392, 180, 96);

      const shopGrad1 = ctx.createLinearGradient(28, 392, 28, 488);
      shopGrad1.addColorStop(0, '#1e293b');
      shopGrad1.addColorStop(1, '#fef08a');
      ctx.fillStyle = shopGrad1;
      ctx.fillRect(32, 396, 172, 88);

      const shopGrad2 = ctx.createLinearGradient(304, 392, 304, 488);
      shopGrad2.addColorStop(0, '#1e293b');
      shopGrad2.addColorStop(1, '#fde68a');
      ctx.fillStyle = shopGrad2;
      ctx.fillRect(308, 396, 172, 88);

      ctx.fillStyle = '#3e2723';
      ctx.fillRect(222, 390, 68, 100);
      ctx.fillStyle = '#1c0d08';
      ctx.fillRect(226, 394, 28, 92);
      ctx.fillRect(258, 394, 28, 92);
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(252, 445, 3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = '#3e2723';
      ctx.fillRect(216, 360, 80, 130);
      ctx.fillStyle = '#1c0d08';
      ctx.fillRect(222, 366, 32, 118);
      ctx.fillRect(258, 366, 32, 118);

      const gWins = [{ x: 50, y: 380 }, { x: 380, y: 380 }];
      for (const gw of gWins) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(gw.x - 2, gw.y - 2, 84, 84);
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(gw.x, gw.y, 80, 80);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(gw.x + 39, gw.y, 2, 80);
        ctx.fillRect(gw.x, gw.y + 39, 80, 2);
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 4;
    return texture;
  }

  public createAltbauFacadeTexture(baseColorHex: number, storeName?: string, awningColor: string = '#b91c1c'): THREE.CanvasTexture {
    if (typeof document === 'undefined') {
      return new THREE.CanvasTexture({} as HTMLCanvasElement);
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.CanvasTexture(canvas);

    const hexStr = '#' + baseColorHex.toString(16).padStart(6, '0');
    ctx.fillStyle = hexStr || '#f1f5f9';
    ctx.fillRect(0, 0, 512, 512);

    // Ground Floor: Rusticated stone bossage
    ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
    for (let y = 330; y < 512; y += 14) {
      ctx.fillRect(0, y, 512, 3);
    }

    // Classical Cornices & Belt Courses
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(0, 320, 512, 10);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(0, 328, 512, 4);

    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(0, 160, 512, 8);
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(0, 166, 512, 3);

    // Dentil Cornice at Roof Line
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(0, 0, 512, 12);
    for (let x = 8; x < 512; x += 16) {
      ctx.fillStyle = '#64748b';
      ctx.fillRect(x, 12, 8, 6);
    }

    // Upper floor windows with pediments
    const cols = 4;
    for (let r = 0; r < 2; r++) {
      const y = r === 0 ? 46 : 196;
      for (let c = 0; c < cols; c++) {
        const x = 32 + c * 122;
        const w = 54;
        const h = 84;

        if (r === 0) {
          ctx.fillStyle = '#cbd5e1';
          ctx.beginPath();
          ctx.moveTo(x - 6, y - 4);
          ctx.lineTo(x + w / 2, y - 20);
          ctx.lineTo(x + w + 6, y - 4);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.fillStyle = '#cbd5e1';
          ctx.fillRect(x - 6, y - 8, w + 12, 6);
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x - 3, y - 2, w + 6, h + 4);

        const winGrad = ctx.createLinearGradient(x, y, x, y + h);
        winGrad.addColorStop(0, '#0f172a');
        winGrad.addColorStop(1, '#38bdf8');
        ctx.fillStyle = winGrad;
        ctx.fillRect(x, y, w, h);

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x + w / 2 - 1, y, 2, h);
        ctx.fillRect(x, y + h * 0.38, w, 2);

        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(x - 6, y + h + 2, w + 12, 6);
      }
    }

    // Ground floor storefront
    if (storeName) {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(24, 340, 464, 36);
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(24, 374, 464, 2);

      ctx.fillStyle = '#f8fafc';
      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(storeName, 256, 358);

      const awningW = 200;
      const awningH = 34;
      const awnings = [{ x: 32, y: 382 }, { x: 280, y: 382 }];
      for (const aw of awnings) {
        for (let s = 0; s < awningW; s += 16) {
          ctx.fillStyle = (s / 16) % 2 === 0 ? awningColor : '#ffffff';
          ctx.fillRect(aw.x + s, aw.y, 16, awningH);
        }
        for (let s = 0; s < awningW; s += 16) {
          ctx.fillStyle = (s / 16) % 2 === 0 ? awningColor : '#ffffff';
          ctx.beginPath();
          ctx.arc(aw.x + s + 8, aw.y + awningH, 8, 0, Math.PI);
          ctx.fill();
        }
      }

      ctx.fillStyle = '#1e293b';
      ctx.fillRect(36, 426, 192, 74);
      ctx.fillRect(284, 426, 192, 74);
      ctx.fillStyle = '#0284c7';
      ctx.fillRect(40, 430, 184, 66);
      ctx.fillRect(288, 430, 184, 66);

      ctx.fillStyle = '#334155';
      ctx.fillRect(238, 400, 36, 100);
      ctx.fillStyle = '#64748b';
      ctx.fillRect(242, 404, 28, 92);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 4;
    return texture;
  }

  public createGabledRoofGeometry(
    w: number,
    d: number,
    roofHeight: number,
    ridgeAlongX: boolean,
    overhang: number = 0.35
  ): { roofGeom: THREE.BufferGeometry; gableGeom: THREE.BufferGeometry } {
    const roofGeom = new THREE.BufferGeometry();
    const gableGeom = new THREE.BufferGeometry();

    if (ridgeAlongX) {
      const rx0 = -w / 2 - overhang;
      const rx1 = w / 2 + overhang;
      const rz0 = -d / 2 - overhang;
      const rz1 = d / 2 + overhang;

      const roofPositions = new Float32Array([
        // North slope (facing +Z)
        rx0, roofHeight, 0,   rx0, 0, rz1,   rx1, 0, rz1,
        rx0, roofHeight, 0,   rx1, 0, rz1,   rx1, roofHeight, 0,
        // South slope (facing -Z)
        rx0, 0, rz0,   rx0, roofHeight, 0,   rx1, roofHeight, 0,
        rx0, 0, rz0,   rx1, roofHeight, 0,   rx1, 0, rz0,
      ]);

      const uScale = (w + 2 * overhang) / 4.0;
      const vScale = Math.hypot(roofHeight, d / 2 + overhang) / 2.0;
      const roofUvs = new Float32Array([
        0, vScale,   0, 0,   uScale, 0,
        0, vScale,   uScale, 0,   uScale, vScale,
        0, 0,   0, vScale,   uScale, vScale,
        0, 0,   uScale, vScale,   uScale, 0,
      ]);

      roofGeom.setAttribute('position', new THREE.BufferAttribute(roofPositions, 3));
      roofGeom.setAttribute('uv', new THREE.BufferAttribute(roofUvs, 2));
      roofGeom.computeVertexNormals();

      const gx0 = -w / 2;
      const gx1 = w / 2;
      const gz0 = -d / 2;
      const gz1 = d / 2;
      const gablePositions = new Float32Array([
        // West gable (facing -X)
        gx0, 0, gz0,   gx0, roofHeight, 0,   gx0, 0, gz1,
        // East gable (facing +X)
        gx1, 0, gz1,   gx1, roofHeight, 0,   gx1, 0, gz0,
      ]);
      const gableUvs = new Float32Array([
        0, 0,   0.5, 1,   1, 0,
        0, 0,   0.5, 1,   1, 0,
      ]);
      gableGeom.setAttribute('position', new THREE.BufferAttribute(gablePositions, 3));
      gableGeom.setAttribute('uv', new THREE.BufferAttribute(gableUvs, 2));
      gableGeom.computeVertexNormals();
    } else {
      const rz0 = -d / 2 - overhang;
      const rz1 = d / 2 + overhang;
      const rx0 = -w / 2 - overhang;
      const rx1 = w / 2 + overhang;

      const roofPositions = new Float32Array([
        // East slope (facing +X)
        0, roofHeight, rz0,   rx1, 0, rz0,   rx1, 0, rz1,
        0, roofHeight, rz0,   rx1, 0, rz1,   0, roofHeight, rz1,
        // West slope (facing -X)
        rx0, 0, rz0,   0, roofHeight, rz0,   0, roofHeight, rz1,
        rx0, 0, rz0,   0, roofHeight, rz1,   rx0, 0, rz1,
      ]);

      const uScale = (d + 2 * overhang) / 4.0;
      const vScale = Math.hypot(roofHeight, w / 2 + overhang) / 2.0;
      const roofUvs = new Float32Array([
        0, vScale,   0, 0,   uScale, 0,
        0, vScale,   uScale, 0,   uScale, vScale,
        0, 0,   0, vScale,   uScale, vScale,
        0, 0,   uScale, vScale,   uScale, 0,
      ]);

      roofGeom.setAttribute('position', new THREE.BufferAttribute(roofPositions, 3));
      roofGeom.setAttribute('uv', new THREE.BufferAttribute(roofUvs, 2));
      roofGeom.computeVertexNormals();

      const gx0 = -w / 2;
      const gx1 = w / 2;
      const gz0 = -d / 2;
      const gz1 = d / 2;
      const gablePositions = new Float32Array([
        // South gable (facing -Z)
        gx1, 0, gz0,   0, roofHeight, gz0,   gx0, 0, gz0,
        // North gable (facing +Z)
        gx0, 0, gz1,   0, roofHeight, gz1,   gx1, 0, gz1,
      ]);
      const gableUvs = new Float32Array([
        0, 0,   0.5, 1,   1, 0,
        0, 0,   0.5, 1,   1, 0,
      ]);
      gableGeom.setAttribute('position', new THREE.BufferAttribute(gablePositions, 3));
      gableGeom.setAttribute('uv', new THREE.BufferAttribute(gableUvs, 2));
      gableGeom.computeVertexNormals();
    }

    return { roofGeom, gableGeom };
  }

  private buildBuildings() {
    const buildingGroup = new THREE.Group();
    const wallColors = [0xfef3c7, 0xfef9c3, 0xf1f5f9, 0xfce7f3, 0xe0f2fe, 0xffedd5];
    const roofTileTex = this.createRoofTileTexture();
    const tileMat = new THREE.MeshStandardMaterial({
      map: roofTileTex,
      roughness: 0.75,
      metalness: 0.05,
    });
    const commRoofMat = new THREE.MeshStandardMaterial({ color: 0x1d4ed8, roughness: 0.5 });
    const flatRoofMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.8 });

    const germanStorefronts = [
      'Bäckerei Müller',
      'St. Georg Apotheke',
      'Fahrschule StVO 3D',
      'Café am Markt',
      'Buchhandlung Goethe',
      'Blumenhaus Rose',
      'Metzgerei & Feinkost',
      'Optik & Akustik',
    ];

    this.buildingBlocks.forEach((b, idx) => {
      const isAltstadt = idx < 8;
      const isInnerSouth = idx >= 8 && idx < 12;
      const isHighrise = idx >= 12 && idx < 16;
      const isCommercial = idx >= 16;

      const height = isHighrise
        ? (24 + (idx % 3) * 5.0)
        : isCommercial
        ? 12.0
        : isInnerSouth
        ? (13.5 + (idx % 2) * 3.0)
        : (10.0 + (idx % 3) * 1.5);

      const baseColor = wallColors[idx % wallColors.length];

      let facadeTex: THREE.CanvasTexture;
      let isPitchedRoof = false;

      if (isCommercial) {
        facadeTex = this.createCommercialFacadeTexture();
        facadeTex.repeat.set(b.w / 14, 1);
      } else if (isHighrise) {
        facadeTex = this.createApartmentTowerTexture(baseColor);
        facadeTex.repeat.set(b.w / 14, height / 12);
      } else if (isAltstadt) {
        isPitchedRoof = true;
        const storeName = germanStorefronts[idx % germanStorefronts.length];
        if (idx % 2 === 0) {
          facadeTex = this.createFachwerkFacadeTexture(baseColor, storeName);
        } else {
          const awningColors = ['#b91c1c', '#1e3a8a', '#047857', '#d97706'];
          facadeTex = this.createAltbauFacadeTexture(baseColor, storeName, awningColors[idx % awningColors.length]);
        }
        facadeTex.repeat.set(b.w / 12, 1);
      } else {
        isPitchedRoof = true;
        facadeTex = this.createAltbauFacadeTexture(baseColor, idx % 2 === 0 ? 'Wohnresidenz' : 'Altstadt-Apotheke');
        facadeTex.repeat.set(b.w / 14, 1);
      }

      const wallMat = new THREE.MeshStandardMaterial({
        map: facadeTex,
        roughness: 0.72,
      });

      const building = new THREE.Mesh(new THREE.BoxGeometry(b.w, height, b.d), wallMat);
      building.position.set(b.x, height / 2, b.z);
      building.castShadow = true;
      building.receiveShadow = true;
      buildingGroup.add(building);

      if (isPitchedRoof) {
        const ridgeAlongX = b.w >= b.d;
        const roofHeight = 4.2;
        const { roofGeom, gableGeom } = this.createGabledRoofGeometry(b.w, b.d, roofHeight, ridgeAlongX, 0.35);

        const roofMesh = new THREE.Mesh(roofGeom, tileMat);
        roofMesh.position.set(b.x, height, b.z);
        roofMesh.castShadow = true;

        const gableMat = new THREE.MeshStandardMaterial({ color: baseColor, roughness: 0.8 });
        const gableMesh = new THREE.Mesh(gableGeom, gableMat);
        gableMesh.position.set(b.x, height, b.z);
        gableMesh.castShadow = true;

        const ridgeGeom = ridgeAlongX
          ? new THREE.CylinderGeometry(0.18, 0.18, b.w + 0.8, 8)
          : new THREE.CylinderGeometry(0.18, 0.18, b.d + 0.8, 8);
        const ridgeMesh = new THREE.Mesh(ridgeGeom, tileMat);
        if (ridgeAlongX) {
          ridgeMesh.rotation.z = Math.PI / 2;
          ridgeMesh.position.set(b.x, height + roofHeight + 0.05, b.z);
        } else {
          ridgeMesh.rotation.x = Math.PI / 2;
          ridgeMesh.position.set(b.x, height + roofHeight + 0.05, b.z);
        }

        const dormerGroup = new THREE.Group();
        const dormerMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6 });
        const dormerGlass = new THREE.MeshStandardMaterial({ color: 0x0284c7, roughness: 0.3 });
        const dormerPositions = ridgeAlongX
          ? [{ x: b.x - b.w * 0.25, z: b.z + b.d * 0.35 }, { x: b.x + b.w * 0.25, z: b.z + b.d * 0.35 }]
          : [{ x: b.x + b.w * 0.35, z: b.z - b.d * 0.25 }, { x: b.x + b.w * 0.35, z: b.z + b.d * 0.25 }];

        for (const dp of dormerPositions) {
          const dormerBox = new THREE.Mesh(new THREE.BoxGeometry(1.2, 1.1, 1.2), dormerMat);
          dormerBox.position.set(dp.x, height + roofHeight * 0.4, dp.z);
          const dormerWin = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.8), dormerGlass);
          dormerWin.position.set(dp.x, height + roofHeight * 0.4, dp.z + (ridgeAlongX ? 0.61 : 0));
          if (!ridgeAlongX) {
            dormerWin.rotation.y = Math.PI / 2;
            dormerWin.position.x = dp.x + 0.61;
            dormerWin.position.z = dp.z;
          }
          dormerGroup.add(dormerBox, dormerWin);
        }

        buildingGroup.add(roofMesh, gableMesh, ridgeMesh, dormerGroup);
      } else {
        const roof = new THREE.Mesh(new THREE.BoxGeometry(b.w * 0.98, 0.5, b.d * 0.98), isCommercial ? commRoofMat : flatRoofMat);
        roof.position.set(b.x, height + 0.25, b.z);
        roof.castShadow = true;
        buildingGroup.add(roof);
      }

      // Solid Static Physics Body in Cannon-es
      const bShape = new CANNON.Box(new CANNON.Vec3(b.w / 2, height / 2, b.d / 2));
      const bBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.staticObstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask:
          COLLISION_GROUP_CHASSIS |
          COLLISION_GROUP_AI_VEHICLE |
          COLLISION_GROUP_DYNAMIC_PROP,
      });
      bBody.addShape(bShape);
      bBody.position.set(b.x, height / 2, b.z);
      this.physicsWorld.world.addBody(bBody);
    });

    this.scene.add(buildingGroup);
    this.buildPerimeterGuardRails();
  }

  private buildPerimeterGuardRails() {
    const railMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.85, roughness: 0.25 });
    const postMat = new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.6, roughness: 0.4 });
    const railGroup = new THREE.Group();

    const postTransforms: THREE.Matrix4[] = [];

    const addRailSide = (start: THREE.Vector3, end: THREE.Vector3) => {
      const len = start.distanceTo(end);
      const mid = start.clone().add(end).multiplyScalar(0.5);
      const dir = end.clone().sub(start).normalize();
      const angleY = Math.atan2(dir.x, dir.z);

      const sideGroup = new THREE.Group();
      sideGroup.position.copy(mid);
      sideGroup.rotation.y = angleY;

      // Continuous steel W-beam rail
      const beamGeom = new THREE.BoxGeometry(0.12, 0.35, len);
      const beam = new THREE.Mesh(beamGeom, railMat);
      beam.position.y = 0.55;
      beam.castShadow = true;
      sideGroup.add(beam);

      // Collect post matrices for instancing
      const numPosts = Math.floor(len / 4.0);
      for (let i = 0; i <= numPosts; i++) {
        const postMat4 = new THREE.Matrix4();
        const localPos = new THREE.Vector3(0, 0.35, -len / 2 + i * 4.0);
        localPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), angleY);
        localPos.add(mid);
        postMat4.makeRotationY(angleY);
        postMat4.setPosition(localPos);
        postTransforms.push(postMat4);
      }

      railGroup.add(sideGroup);

      // Solid Static Physics Body for Guard Rail
      const railShape = new CANNON.Box(new CANNON.Vec3(0.2, 0.5, len / 2));
      const railBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.staticObstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      railBody.addShape(railShape);
      railBody.position.set(mid.x, 0.5, mid.z);
      railBody.quaternion.setFromEuler(0, angleY, 0);
      this.physicsWorld.world.addBody(railBody);
    };

    // 4 Outer Perimeter Boundaries
    addRailSide(new THREE.Vector3(-126, 0, 126), new THREE.Vector3(126, 0, 126));
    addRailSide(new THREE.Vector3(-126, 0, -126), new THREE.Vector3(126, 0, -126));
    addRailSide(new THREE.Vector3(126, 0, -126), new THREE.Vector3(126, 0, 126));
    addRailSide(new THREE.Vector3(-126, 0, -126), new THREE.Vector3(-126, 0, 126));

    // High-performance InstancedMesh for all guardrail posts (1 draw call)
    if (postTransforms.length > 0) {
      const postGeom = new THREE.BoxGeometry(0.14, 0.75, 0.14);
      const instancedPosts = new THREE.InstancedMesh(postGeom, postMat, postTransforms.length);
      instancedPosts.castShadow = true;
      for (let i = 0; i < postTransforms.length; i++) {
        instancedPosts.setMatrixAt(i, postTransforms[i]);
      }
      instancedPosts.instanceMatrix.needsUpdate = true;
      railGroup.add(instancedPosts);
    }

    this.scene.add(railGroup);
  }

  public isLocationClearForTree(x: number, z: number): boolean {
    // 1. Must be strictly clear of all road segments (road half-width + minimum 1.2m clearance)
    for (const seg of this.roadNetwork.segments) {
      const dx = seg.end.x - seg.start.x;
      const dz = seg.end.z - seg.start.z;
      const lenSq = dx * dx + dz * dz;
      let dist: number;
      if (lenSq === 0) {
        dist = Math.hypot(x - seg.start.x, z - seg.start.z);
      } else {
        let t = ((x - seg.start.x) * dx + (z - seg.start.z) * dz) / lenSq;
        t = Math.max(0, Math.min(1, t));
        dist = Math.hypot(x - (seg.start.x + t * dx), z - (seg.start.z + t * dz));
      }
      if (dist < seg.width / 2 + 1.2) {
        return false;
      }
    }

    // 2. Must be clear of roundabout (outerRadius 18.0 + 2.5m clearance)
    if (Math.hypot(x - this.roundabout.center.x, z - this.roundabout.center.z) < this.roundabout.outerRadius + 2.5) {
      return false;
    }

    // 3. Must be clear of intersections (curbs, turning radii, pedestrian crossings)
    const intersectionCenters = [
      { x: 0, z: 60, r: 6.5 },
      { x: 0, z: -60, r: 6.5 },
      { x: 60, z: 0, r: 6.5 },
      { x: -60, z: 0, r: 6.5 },
      { x: 50, z: 60, r: 5.5 },
      { x: -50, z: 60, r: 5.5 },
      { x: 50, z: -60, r: 5.5 },
      { x: -50, z: -60, r: 5.5 },
      { x: 50, z: 30, r: 5.5 },
      { x: -50, z: 30, r: 5.5 },
      { x: 50, z: 0, r: 6.5 },
      { x: -50, z: 0, r: 6.5 },
      // Ring intersections and T-junctions
      { x: 0, z: 120, r: 6.5 },
      { x: 0, z: -120, r: 6.5 },
      { x: 120, z: 0, r: 6.5 },
      { x: -120, z: 0, r: 6.5 },
      { x: 50, z: 120, r: 6.0 },
      { x: -50, z: 120, r: 6.0 },
      { x: 50, z: -120, r: 6.0 },
      { x: -50, z: -120, r: 6.0 },
      { x: 120, z: 60, r: 6.0 },
      { x: -120, z: 60, r: 6.0 },
      { x: 120, z: -60, r: 6.0 },
      { x: -120, z: -60, r: 6.0 },
      { x: 120, z: 120, r: 7.0 },
      { x: -120, z: 120, r: 7.0 },
      { x: 120, z: -120, r: 7.0 },
      { x: -120, z: -120, r: 7.0 },
    ];
    for (const inter of intersectionCenters) {
      if (Math.hypot(x - inter.x, z - inter.z) < inter.r + 2.0) {
        return false;
      }
    }

    // 4. Must be clear of buildings
    for (const b of this.buildingBlocks) {
      if (
        x >= b.x - b.w / 2 - 0.8 &&
        x <= b.x + b.w / 2 + 0.8 &&
        z >= b.z - b.d / 2 - 0.8 &&
        z <= b.z + b.d / 2 + 0.8
      ) {
        return false;
      }
    }

    // 5. Must be clear of special infrastructure (bus stop, parking bays, wenden zone)
    if (Math.hypot(x - 5.5, z - 15) < 6.0) return false;
    if (x >= 18 && x <= 25 && z >= -54 && z <= -26) return false; // Parallel bay
    if (x >= 18 && x <= 25 && z >= -84 && z <= -56) return false; // Bay parking
    if (Math.hypot(x - (-40), z - (-40)) < 14.0) return false; // Wenden zone

    return true;
  }

  private buildTreesAndProps() {
    const propGroup = new THREE.Group();
    const trunkMat = new THREE.MeshStandardMaterial({ color: 0x3e2723 });
    const leavesMat = new THREE.MeshStandardMaterial({ color: 0x15803d, roughness: 0.8 });

    const candidates: Array<{ x: number; z: number }> = [];

    // A. Boulevard North-South (x = ±6.2)
    // North branch (z: 24..52 and 72..112)
    const nZ = [24, 34, 48, 72, 84, 96, 108];
    for (const z of nZ) {
      candidates.push({ x: 6.2, z });
      candidates.push({ x: -6.2, z });
    }
    // South branch (z: -24..-52 and -72..-112)
    const sZ = [-24, -34, -48, -72, -84, -96, -108];
    for (const z of sZ) {
      candidates.push({ x: 6.2, z });
      candidates.push({ x: -6.2, z });
    }

    // B. Avenue East-West (z = ±6.2)
    // East branch (x: 24..40 and 72..108, strictly clear of cross street at x=50 and intersection at x=60)
    const eX = [24, 32, 40, 72, 84, 96, 108];
    for (const x of eX) {
      candidates.push({ x, z: 6.2 });
      candidates.push({ x, z: -6.2 });
    }
    // West branch (x: -24..-40 and -72..-108, strictly clear of cross street at x=-50 and intersection at x=-60)
    const wX = [-24, -32, -40, -72, -84, -96, -108];
    for (const x of wX) {
      candidates.push({ x, z: 6.2 });
      candidates.push({ x, z: -6.2 });
    }

    // C. North Residential Street Sidewalk verges (z = 66.0 & z = 54.0)
    const resX = [-95, -80, -32, -18, 18, 32, 80, 95];
    for (const x of resX) {
      candidates.push({ x, z: 66.0 });
      candidates.push({ x, z: 54.0 });
    }

    // D. South Residential Street Sidewalk verges (z = -66.0 & z = -54.0)
    for (const x of resX) {
      candidates.push({ x, z: -66.0 });
      candidates.push({ x, z: -54.0 });
    }

    // Strictly validate every candidate location
    const validPositions: Array<{ x: number; z: number }> = [];
    this.treePositions = [];
    for (const cand of candidates) {
      if (this.isLocationClearForTree(cand.x, cand.z)) {
        validPositions.push(cand);
        this.treePositions.push(new THREE.Vector3(cand.x, 0, cand.z));
      }
    }

    if (validPositions.length === 0) return;

    // Instanced meshes for tree trunks and foliage (2 draw calls total)
    const trunkGeom = new THREE.CylinderGeometry(0.16, 0.22, 2.8, 8);
    const leavesGeom = new THREE.DodecahedronGeometry(1.6);
    const instancedTrunks = new THREE.InstancedMesh(trunkGeom, trunkMat, validPositions.length);
    const instancedFoliage = new THREE.InstancedMesh(leavesGeom, leavesMat, validPositions.length);
    instancedTrunks.castShadow = true;
    instancedFoliage.castShadow = true;

    const dummy = new THREE.Object3D();
    validPositions.forEach((tp, idx) => {
      dummy.position.set(tp.x, 1.4, tp.z);
      dummy.rotation.set(0, (idx * 0.7) % (Math.PI * 2), 0);
      dummy.updateMatrix();
      instancedTrunks.setMatrixAt(idx, dummy.matrix);

      dummy.position.set(tp.x, 3.2, tp.z);
      dummy.updateMatrix();
      instancedFoliage.setMatrixAt(idx, dummy.matrix);

      // Static tree trunk collision body
      const trunkShape = new CANNON.Cylinder(0.24, 0.24, 2.8, 8);
      const treeBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.staticObstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      treeBody.addShape(trunkShape);
      treeBody.position.set(tp.x, 1.4, tp.z);
      this.physicsWorld.world.addBody(treeBody);
    });

    instancedTrunks.instanceMatrix.needsUpdate = true;
    instancedFoliage.instanceMatrix.needsUpdate = true;
    propGroup.add(instancedTrunks, instancedFoliage);

    this.scene.add(propGroup);
  }

  public buildStreetLamps() {
    const lampGroup = new THREE.Group();
    const mastMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.3 });
    const lampLightMat = new THREE.MeshStandardMaterial({
      color: 0xfef08a,
      emissive: 0xfef08a,
      emissiveIntensity: 2.2,
    });

    const lampPositions = [
      { x: 5.6, z: -80, rotY: 0 },
      { x: 5.6, z: -40, rotY: 0 },
      { x: 5.6, z: 40, rotY: 0 },
      { x: 5.6, z: 80, rotY: 0 },
      { x: -5.6, z: -80, rotY: Math.PI },
      { x: -5.6, z: -40, rotY: Math.PI },
      { x: -5.6, z: 40, rotY: Math.PI },
      { x: -5.6, z: 80, rotY: Math.PI },
      { x: -80, z: 5.6, rotY: -Math.PI / 2 },
      { x: -40, z: 5.6, rotY: -Math.PI / 2 },
      { x: 40, z: 5.6, rotY: -Math.PI / 2 },
      { x: 80, z: 5.6, rotY: -Math.PI / 2 },
      { x: -80, z: -5.6, rotY: Math.PI / 2 },
      { x: -40, z: -5.6, rotY: Math.PI / 2 },
      { x: 40, z: -5.6, rotY: Math.PI / 2 },
      { x: 80, z: -5.6, rotY: Math.PI / 2 },
    ];

    const mastGeom = new THREE.CylinderGeometry(0.06, 0.08, 5.4, 12);
    const armGeom = new THREE.CylinderGeometry(0.045, 0.055, 1.8, 12);
    const headGeom = new THREE.BoxGeometry(0.26, 0.08, 0.44);

    lampPositions.forEach((lp) => {
      const singleLamp = new THREE.Group();
      singleLamp.position.set(lp.x, 0, lp.z);
      singleLamp.rotation.y = lp.rotY;

      // Vertical Steel Mast
      const mast = new THREE.Mesh(mastGeom, mastMat);
      mast.position.y = 2.7;
      mast.castShadow = true;

      // Curved Arc Arm extending over road (matching reference image)
      const arm = new THREE.Mesh(armGeom, mastMat);
      arm.position.set(0, 5.65, -0.7);
      arm.rotation.x = -Math.PI / 4.2;

      // Glowing Luminaire Head
      const head = new THREE.Mesh(headGeom, lampLightMat);
      head.position.set(0, 6.10, -1.35);
      head.rotation.x = 0.08;

      singleLamp.add(mast, arm, head);
      lampGroup.add(singleLamp);

      // Physical mast collider (Solid steel post stopping vehicle impacts)
      const mastBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.staticObstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      mastBody.addShape(new CANNON.Cylinder(0.12, 0.12, 5.4, 8));
      mastBody.position.set(lp.x, 2.7, lp.z);
      this.physicsWorld.world.addBody(mastBody);
    });

    this.scene.add(lampGroup);
  }

  private buildTrafficLights() {
    // ── Intersection 1: Main Boulevard & North Cross Street (0, 0, 60) ────────
    // Each traffic light stands on the near-right sidewalk before the intersection,
    // facing oncoming traffic with its overhead cantilever arm extending over the roadway.
    const tl1 = new TrafficLight(new THREE.Vector3(-4.8, 0, 54), Math.PI, 0);        // Northbound approach (traveling +Z)
    tl1.addSecondaryHead(new THREE.Vector3(-4.8, 0, 66), Math.PI); // Far-side right repeater across junction
    tl1.addSecondaryHead(new THREE.Vector3(4.8, 0, 54), Math.PI);  // Near-side left curb repeater

    const tl2 = new TrafficLight(new THREE.Vector3(4.8, 0, 66), 0, 0);               // Southbound approach (traveling -Z)
    tl2.addSecondaryHead(new THREE.Vector3(4.8, 0, 54), 0);        // Far-side right repeater across junction
    tl2.addSecondaryHead(new THREE.Vector3(-4.8, 0, 66), 0);       // Near-side left curb repeater

    const tl3 = new TrafficLight(new THREE.Vector3(10.0, 0, 55.2), Math.PI / 2, 16); // -X approach (right sidewalk z=55.2)
    tl3.addSecondaryHead(new THREE.Vector3(-10.0, 0, 55.2), Math.PI / 2); // Far-side right repeater
    tl3.addSecondaryHead(new THREE.Vector3(10.0, 0, 64.8), Math.PI / 2);  // Near-side left repeater

    const tl4 = new TrafficLight(new THREE.Vector3(-10.0, 0, 64.8), -Math.PI / 2, 16);// +X approach (right sidewalk z=64.8)
    tl4.addSecondaryHead(new THREE.Vector3(10.0, 0, 64.8), -Math.PI / 2);  // Far-side right repeater
    tl4.addSecondaryHead(new THREE.Vector3(-10.0, 0, 55.2), -Math.PI / 2); // Near-side left repeater

    // ── Intersection 2: Main Boulevard & South Cross Street (0, 0, -60) ────────
    const tl5 = new TrafficLight(new THREE.Vector3(-4.8, 0, -66), Math.PI, 0);       // Northbound approach
    tl5.addSecondaryHead(new THREE.Vector3(-4.8, 0, -54), Math.PI);
    tl5.addSecondaryHead(new THREE.Vector3(4.8, 0, -66), Math.PI);

    const tl6 = new TrafficLight(new THREE.Vector3(4.8, 0, -54), 0, 0);              // Southbound approach
    tl6.addSecondaryHead(new THREE.Vector3(4.8, 0, -66), 0);
    tl6.addSecondaryHead(new THREE.Vector3(-4.8, 0, -54), 0);

    const tl7 = new TrafficLight(new THREE.Vector3(10.0, 0, -64.8), Math.PI / 2, 16); // -X approach (right sidewalk z=-64.8)
    tl7.addSecondaryHead(new THREE.Vector3(-10.0, 0, -64.8), Math.PI / 2);
    tl7.addSecondaryHead(new THREE.Vector3(10.0, 0, -55.2), Math.PI / 2);

    const tl8 = new TrafficLight(new THREE.Vector3(-10.0, 0, -55.2), -Math.PI / 2, 16);// +X approach (right sidewalk z=-55.2)
    tl8.addSecondaryHead(new THREE.Vector3(10.0, 0, -55.2), -Math.PI / 2);
    tl8.addSecondaryHead(new THREE.Vector3(-10.0, 0, -64.8), -Math.PI / 2);

    // ── Intersection 3: East Avenue & Cross Street (60, 0, 0) ─────────────────
    const tl9  = new TrafficLight(new THREE.Vector3(54, 0, 4.8), -Math.PI / 2, 8);   // +X approach (right sidewalk z=+4.8)
    tl9.addSecondaryHead(new THREE.Vector3(66, 0, 4.8), -Math.PI / 2);
    tl9.addSecondaryHead(new THREE.Vector3(54, 0, -4.8), -Math.PI / 2);

    const tl10 = new TrafficLight(new THREE.Vector3(66, 0, -4.8), Math.PI / 2, 8);   // -X approach (right sidewalk z=-4.8)
    tl10.addSecondaryHead(new THREE.Vector3(54, 0, -4.8), Math.PI / 2);
    tl10.addSecondaryHead(new THREE.Vector3(66, 0, 4.8), Math.PI / 2);

    const tl11 = new TrafficLight(new THREE.Vector3(64.8, 0, 10.0), 0, 24);          // Southbound approach
    tl11.addSecondaryHead(new THREE.Vector3(64.8, 0, -10.0), 0);
    tl11.addSecondaryHead(new THREE.Vector3(55.2, 0, 10.0), 0);

    const tl12 = new TrafficLight(new THREE.Vector3(55.2, 0, -10.0), Math.PI, 24);  // Northbound approach
    tl12.addSecondaryHead(new THREE.Vector3(55.2, 0, 10.0), Math.PI);
    tl12.addSecondaryHead(new THREE.Vector3(64.8, 0, -10.0), Math.PI);

    // ── Intersection 4: West Avenue & Cross Street (-60, 0, 0) ────────────────
    const tl13 = new TrafficLight(new THREE.Vector3(-66, 0, 4.8), -Math.PI / 2, 8);  // +X approach (right sidewalk z=+4.8)
    tl13.addSecondaryHead(new THREE.Vector3(-54, 0, 4.8), -Math.PI / 2);
    tl13.addSecondaryHead(new THREE.Vector3(-66, 0, -4.8), -Math.PI / 2);

    const tl14 = new TrafficLight(new THREE.Vector3(-54, 0, -4.8), Math.PI / 2, 8);  // -X approach (right sidewalk z=-4.8)
    tl14.addSecondaryHead(new THREE.Vector3(-66, 0, -4.8), Math.PI / 2);
    tl14.addSecondaryHead(new THREE.Vector3(-54, 0, 4.8), Math.PI / 2);

    const tl15 = new TrafficLight(new THREE.Vector3(-55.2, 0, 10.0), 0, 24);         // Southbound approach
    tl15.addSecondaryHead(new THREE.Vector3(-55.2, 0, -10.0), 0);
    tl15.addSecondaryHead(new THREE.Vector3(-64.8, 0, 10.0), 0);

    const tl16 = new TrafficLight(new THREE.Vector3(-64.8, 0, -10.0), Math.PI, 24); // Northbound approach
    tl16.addSecondaryHead(new THREE.Vector3(-64.8, 0, 10.0), Math.PI);
    tl16.addSecondaryHead(new THREE.Vector3(-55.2, 0, -10.0), Math.PI);

    this.trafficLights.push(
      tl1, tl2, tl3, tl4,
      tl5, tl6, tl7, tl8,
      tl9, tl10, tl11, tl12,
      tl13, tl14, tl15, tl16
    );
    this.trafficLights.forEach((tl) => {
      this.scene.add(tl.group);

      // Static pole physics body for traffic light

      const pBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.staticObstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      pBody.addShape(new CANNON.Cylinder(0.18, 0.18, 3.5, 8));
      pBody.position.set(tl.group.position.x, 1.75, tl.group.position.z);
      this.physicsWorld.world.addBody(pBody);
    });

  }

  private buildSignPosts() {
    const signDefs = [
      // 1. German Town Entrance (Ortstafel Z 310)
      { type: 'Z310_ORTSTAFEL', pos: new THREE.Vector3(5.5, 0, 110), rotY: 0 },
      { type: 'Z310_ORTSTAFEL', pos: new THREE.Vector3(-5.5, 0, -110), rotY: Math.PI },

      // 2. Vorfahrtsstraße (Z 306)
      // Placed on priority roads outside intersection/roundabout zones (NOT at roundabout entries!)
      { type: 'Z306_VORFAHRTSSTRASSE', pos: new THREE.Vector3(5.5, 0, 95), rotY: 0 },
      { type: 'Z306_VORFAHRTSSTRASSE', pos: new THREE.Vector3(-5.5, 0, -95), rotY: Math.PI },
      { type: 'Z306_VORFAHRTSSTRASSE', pos: new THREE.Vector3(95, 0, -5.5), rotY: Math.PI / 2 },
      { type: 'Z306_VORFAHRTSSTRASSE', pos: new THREE.Vector3(-95, 0, 5.5), rotY: -Math.PI / 2 },

      // 3. Tempo 30 Zone Signs (German StVO: Dual-sided entrance/exit portals)
      // Junction (0, 60): East branch (x > 0)
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(8.0, 0, 63.5), rotY: -Math.PI / 2 },
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(8.0, 0, 56.5), rotY: -Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(7.9, 0, 56.5), rotY: Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(7.9, 0, 63.5), rotY: Math.PI / 2 },

      // Junction (0, 60): West branch (x < 0)
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(-8.0, 0, 56.5), rotY: Math.PI / 2 },
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(-8.0, 0, 63.5), rotY: Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(-7.9, 0, 63.5), rotY: -Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(-7.9, 0, 56.5), rotY: -Math.PI / 2 },

      // Cross street entrances from Main Avenue (z = 0):
      // East cross street (x = 50):
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(53.5, 0, 15), rotY: Math.PI },
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(46.5, 0, 15), rotY: Math.PI },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(46.5, 0, 14.9), rotY: 0 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(53.5, 0, 14.9), rotY: 0 },

      // West cross street (x = -50):
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(-46.5, 0, 15), rotY: Math.PI },
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(-53.5, 0, 15), rotY: Math.PI },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(-53.5, 0, 14.9), rotY: 0 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(-46.5, 0, 14.9), rotY: 0 },

      // South residential street entrances (z = -60):
      // Junction (0, -60): East branch (x > 0):
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(8.0, 0, -63.5), rotY: -Math.PI / 2 },
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(8.0, 0, -56.5), rotY: -Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(7.9, 0, -56.5), rotY: Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(7.9, 0, -63.5), rotY: Math.PI / 2 },

      // Junction (0, -60): West branch (x < 0):
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(-8.0, 0, -56.5), rotY: Math.PI / 2 },
      { type: 'Z274_1_ZONE_30_BEGINN', pos: new THREE.Vector3(-8.0, 0, -63.5), rotY: Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(-7.9, 0, -63.5), rotY: -Math.PI / 2 },
      { type: 'Z274_2_ZONE_30_ENDE', pos: new THREE.Vector3(-7.9, 0, -56.5), rotY: -Math.PI / 2 },

      // 4. Rechts-vor-Links Danger Warnings (Zeichen 102 - § 8 StVO / VwV-StVO)
      // Placed 25m–35m before unmarked residential intersections on right curb facing approaching traffic:
      // Intersection (50, 0, 60):
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(78, 0, 56.0), rotY: Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(22, 0, 64.0), rotY: -Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(54.0, 0, 88), rotY: 0 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(46.0, 0, 32), rotY: Math.PI },

      // Intersection (-50, 0, 60):
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-22, 0, 56.0), rotY: Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-78, 0, 64.0), rotY: -Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-46.0, 0, 88), rotY: 0 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-54.0, 0, 32), rotY: Math.PI },

      // Intersection (50, 0, -60):
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(78, 0, -64.0), rotY: Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(22, 0, -56.0), rotY: -Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(54.0, 0, -32), rotY: 0 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(46.0, 0, -88), rotY: Math.PI },

      // Intersection (-50, 0, -60):
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-22, 0, -64.0), rotY: Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-78, 0, -56.0), rotY: -Math.PI / 2 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-46.0, 0, -32), rotY: 0 },
      { type: 'Z102_KREUZUNG_RVL', pos: new THREE.Vector3(-54.0, 0, -88), rotY: Math.PI },

      // 5. Traffic Lights Ahead Warnings (Zeichen 131 - VwV-StVO zu § 40)
      // Placed 28m–32m before signalized stop lines on right curb:
      // Intersection (0, 0, 60):
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(-5.5, 0, 28), rotY: Math.PI },
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(5.5, 0, 92), rotY: 0 },
      // Intersection (0, 0, -60):
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(-5.5, 0, -92), rotY: Math.PI },
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(5.5, 0, -28), rotY: 0 },
      // Intersection (60, 0, 0):
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(28, 0, 5.5), rotY: -Math.PI / 2 },
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(92, 0, -5.5), rotY: Math.PI / 2 },
      // Intersection (-60, 0, 0):
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(-92, 0, 5.5), rotY: -Math.PI / 2 },
      { type: 'Z131_LICHTZEICHENANLAGE', pos: new THREE.Vector3(-28, 0, -5.5), rotY: Math.PI / 2 },

      // 6. Zebrastreifen & Pedestrian Signs (Zeichen 350 & Zeichen 134)
      // Boulevard crossing (0, 0, 42):
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(5.5, 0, 42), rotY: 0 },
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(-5.5, 0, 42), rotY: Math.PI },
      { type: 'Z134_FUSSGAENGER_WARNUNG', pos: new THREE.Vector3(-5.5, 0, 28), rotY: Math.PI },
      { type: 'Z134_FUSSGAENGER_WARNUNG', pos: new THREE.Vector3(5.5, 0, 56), rotY: 0 },

      // Boulevard crossing (0, 0, 78):
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(5.5, 0, 78), rotY: 0 },
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(-5.5, 0, 78), rotY: Math.PI },
      { type: 'Z134_FUSSGAENGER_WARNUNG', pos: new THREE.Vector3(5.5, 0, 92), rotY: 0 },
      { type: 'Z134_FUSSGAENGER_WARNUNG', pos: new THREE.Vector3(-5.5, 0, 64), rotY: Math.PI },

      // Residential crossings:
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(30, 0, 64.5), rotY: -Math.PI / 2 },
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(30, 0, 55.5), rotY: Math.PI / 2 },
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(-30, 0, 64.5), rotY: -Math.PI / 2 },
      { type: 'Z350_FUSSGAENGERUEBERWEG', pos: new THREE.Vector3(-30, 0, 55.5), rotY: Math.PI / 2 },

      // 7. Curve Warnings
      { type: 'Z103_KURVE_RECHTS', pos: new THREE.Vector3(5.5, 0, 88), rotY: 0 },
      { type: 'Z103_KURVE_LINKS', pos: new THREE.Vector3(-5.5, 0, -88), rotY: Math.PI },
      { type: 'Z105_DOPPELKURVE', pos: new THREE.Vector3(68, 0, -5.5), rotY: Math.PI / 2 },
      { type: 'Z625_KURVENLEITTAFEL_RECHTS', pos: new THREE.Vector3(6.5, 0, 102), rotY: -Math.PI / 4 },
      { type: 'Z625_KURVENLEITTAFEL_LINKS', pos: new THREE.Vector3(-6.5, 0, -102), rotY: (3 * Math.PI) / 4 },

      // 8. Bicycle Warnings & Paths
      { type: 'Z138_RADVERKEHR', pos: new THREE.Vector3(-5.5, 0, 30), rotY: Math.PI },
      { type: 'Z237_RADWEG', pos: new THREE.Vector3(-5.5, 0, 18), rotY: Math.PI },

      // 9. Railway Crossing Approach Beacons (Bahnübergang on Ring North at x=70, z=120)
      // Eastbound approach (moving +X, right curb z=124.5, facing West):
      { type: 'Z156_BAKE_3_STREIFEN', pos: new THREE.Vector3(10, 0, 124.5), rotY: -Math.PI / 2 },
      { type: 'Z159_BAKE_2_STREIFEN', pos: new THREE.Vector3(30, 0, 124.5), rotY: -Math.PI / 2 },
      { type: 'Z162_BAKE_1_STREIFEN', pos: new THREE.Vector3(50, 0, 124.5), rotY: -Math.PI / 2 },
      // Westbound approach (moving -X, right curb z=115.5, facing East):
      { type: 'Z162_BAKE_1_STREIFEN', pos: new THREE.Vector3(90, 0, 115.5), rotY: Math.PI / 2 },
      { type: 'Z159_BAKE_2_STREIFEN', pos: new THREE.Vector3(110, 0, 115.5), rotY: Math.PI / 2 },

      // 10. T-Junction STOP (Z206) & YIELD (Z205) Signs at Outer Ring Entries (120m)
      // North Ring entries (z = 120):
      { type: 'Z206_STOPP', pos: new THREE.Vector3(46.5, 0, 114), rotY: Math.PI },
      { type: 'Z206_STOPP', pos: new THREE.Vector3(-53.5, 0, 114), rotY: Math.PI },
      { type: 'Z205_VORFAHRT_GEWAEHREN', pos: new THREE.Vector3(-5.5, 0, 114), rotY: Math.PI },

      // South Ring entries (z = -120):
      { type: 'Z206_STOPP', pos: new THREE.Vector3(53.5, 0, -114), rotY: 0 },
      { type: 'Z206_STOPP', pos: new THREE.Vector3(-46.5, 0, -114), rotY: 0 },
      { type: 'Z205_VORFAHRT_GEWAEHREN', pos: new THREE.Vector3(5.5, 0, -114), rotY: 0 },

      // East Ring entries (x = 120):
      { type: 'Z206_STOPP', pos: new THREE.Vector3(114, 0, 64.0), rotY: -Math.PI / 2 },
      { type: 'Z206_STOPP', pos: new THREE.Vector3(114, 0, -56.0), rotY: -Math.PI / 2 },
      { type: 'Z205_VORFAHRT_GEWAEHREN', pos: new THREE.Vector3(114, 0, 5.5), rotY: -Math.PI / 2 },

      // West Ring entries (x = -120):
      { type: 'Z206_STOPP', pos: new THREE.Vector3(-114, 0, 56.0), rotY: Math.PI / 2 },
      { type: 'Z206_STOPP', pos: new THREE.Vector3(-114, 0, -64.0), rotY: Math.PI / 2 },
      { type: 'Z205_VORFAHRT_GEWAEHREN', pos: new THREE.Vector3(-114, 0, -5.5), rotY: Math.PI / 2 },

      // 11. Warnings & Construction
      { type: 'Z136_ACHTUNG_KINDER', pos: new THREE.Vector3(35, 0, 64.0), rotY: -Math.PI / 2 },
      { type: 'Z101_GEFAHRSTELLE', pos: new THREE.Vector3(-35, 0, 56.0), rotY: Math.PI / 2 },
      { type: 'Z123_BAUSTELLE', pos: new THREE.Vector3(54.0, 0, -75), rotY: 0 },
      { type: 'Z301_VORFAHRT_KREUZUNG', pos: new THREE.Vector3(46.0, 0, -45), rotY: Math.PI },

      // 12. Halteverbot & Überholverbot
      { type: 'Z283_ABSOLUTES_HALTEVERBOT', pos: new THREE.Vector3(5.5, 0, 52), rotY: 0 },
      { type: 'Z286_EINGESCHRAENKTES_HALTEVERBOT', pos: new THREE.Vector3(5.5, 0, 68), rotY: 0 },
      { type: 'Z276_UEBERHOLVERBOT', pos: new THREE.Vector3(5.5, 0, 32), rotY: 0 },

      // 13. Bus Stop & Parking
      { type: 'Z224_HALTESTELLE', pos: new THREE.Vector3(5.5, 0, 15), rotY: 0 },
      { type: 'Z314_PARKEN', pos: new THREE.Vector3(24, 0, -5.5), rotY: Math.PI / 2 },
      { type: 'Z357_SACKGASSE', pos: new THREE.Vector3(-46, 0, 75), rotY: Math.PI },

      // 14. Street Name Signs
      { type: 'STRASSE_HAUPTSTRASSE', pos: new THREE.Vector3(5.8, 0, 62), rotY: 0 },
      { type: 'STRASSE_GOETHESTRASSE', pos: new THREE.Vector3(8.5, 0, 58), rotY: Math.PI / 2 },
      { type: 'STRASSE_BAHNHOFSTRASSE', pos: new THREE.Vector3(-8.5, 0, 58), rotY: -Math.PI / 2 },
      { type: 'STRASSE_SCHULSTRASSE', pos: new THREE.Vector3(48, 0, 62), rotY: 0 },

      // 15. Vorbeifahrt rechts (on splitter island)
      { type: 'Z222_VORBEIFAHRT_RECHTS', pos: new THREE.Vector3(0, 0, 19.5), rotY: 0 },
    ];


    for (const d of signDefs) {
      const mesh = TrafficSigns.createSign(d.type as any, d.pos, d.rotY);
      this.scene.add(mesh);

      // Breakaway sign post: starts STATIC firmly anchored into the ground/sidewalk.
      // When impacted by vehicle chassis, it becomes DYNAMIC (mass 35kg) and tumbles realistically.
      const propShape = new CANNON.Cylinder(0.10, 0.10, 2.4, 8);
      const propBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        mass: 0,
        material: this.physicsWorld.dynamicPropMaterial,
        linearDamping: 0.15,
        angularDamping: 0.40,
        collisionFilterGroup: COLLISION_GROUP_DYNAMIC_PROP,
        collisionFilterMask:
          COLLISION_GROUP_GROUND |
          COLLISION_GROUP_CHASSIS |
          COLLISION_GROUP_AI_VEHICLE |
          COLLISION_GROUP_STATIC,
      });
      propBody.addShape(propShape, new CANNON.Vec3(0, 1.2, 0));
      propBody.position.set(d.pos.x, 0, d.pos.z);
      propBody.quaternion.setFromEuler(0, d.rotY, 0);

      // Breakaway trigger: upon vehicle collision, release to dynamic body with gravity
      propBody.addEventListener('collide', (e: any) => {
        if (propBody.type === CANNON.Body.STATIC) {
          const other: CANNON.Body = e.body;
          if (other && (other.collisionFilterGroup === COLLISION_GROUP_CHASSIS || other.collisionFilterGroup === COLLISION_GROUP_AI_VEHICLE)) {
            propBody.type = CANNON.Body.DYNAMIC;
            propBody.mass = 35;
            propBody.updateMassProperties();

            const v = other.velocity;
            const speed = v.length();
            if (speed > 1.0) {
              const impulseDir = v.unit();
              propBody.applyImpulse(impulseDir.scale(Math.max(60, speed * 25)), new CANNON.Vec3(0, 1.5, 0));
            }
          }
        }
      });

      this.physicsWorld.world.addBody(propBody);
      this.dynamicProps.push({ mesh, body: propBody });
    }
  }

  private buildMotorwayInfrastructure() {
    // 1. Guardrails (Leitplanken) along the median of each 4 sides of the Autobahn loop
    const railMat = new THREE.MeshStandardMaterial({
      color: 0x9ca3af,
      metalness: 0.9,
      roughness: 0.3,
    });

    const guardrails = [
      // North side median (z = -580, runs x: -560..560)
      { pos: new THREE.Vector3(0, 0.25, -580), size: new THREE.Vector3(1120, 0.5, 0.2) },
      // South side median (z = 580, runs x: -560..560)
      { pos: new THREE.Vector3(0, 0.25, 580), size: new THREE.Vector3(1120, 0.5, 0.2) },
      // East side median (x = 580, runs z: -560..560)
      { pos: new THREE.Vector3(580, 0.25, 0), size: new THREE.Vector3(0.2, 0.5, 1120) },
      // West side median (x = -580, runs z: -560..560)
      { pos: new THREE.Vector3(-580, 0.25, 0), size: new THREE.Vector3(0.2, 0.5, 1120) },
    ];

    for (const gr of guardrails) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(gr.size.x, gr.size.y, gr.size.z), railMat);
      mesh.position.copy(gr.pos);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);

      const body = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.obstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      body.addShape(new CANNON.Box(new CANNON.Vec3(gr.size.x / 2, gr.size.y / 2, gr.size.z / 2)));
      body.position.set(gr.pos.x, gr.pos.y, gr.pos.z);
      this.physicsWorld.world.addBody(body);
    }

    // 2. Overhead Gantries (Schilderbrücken) along North side (z = -580)
    const gantryPositionsX = [-400, -200, 0, 200, 400];
    const postMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.3 });
    const beamMat = new THREE.MeshStandardMaterial({ color: 0x1d4ed8, roughness: 0.5 });

    for (const gx of gantryPositionsX) {
      const postGeom = new THREE.CylinderGeometry(0.15, 0.15, 7.0, 12);
      const postL = new THREE.Mesh(postGeom, postMat);
      postL.position.set(gx, 3.5, -580 - 10);
      postL.castShadow = true;

      const postR = new THREE.Mesh(postGeom, postMat);
      postR.position.set(gx, 3.5, -580 + 10);
      postR.castShadow = true;

      const beam = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 20.0), beamMat);
      beam.position.set(gx, 6.8, -580);
      beam.castShadow = true;

      this.scene.add(postL, postR, beam);
    }

    // 3. Autobahn signs (Z330_1 Beginn, Z330_2 Ende) at the 4 interchanges
    const abSigns: Array<{ type: any; pos: THREE.Vector3; rotY: number }> = [
      // North interchange
      { type: 'Z330_1_AUTOBAHN_BEGINN', pos: new THREE.Vector3(4, 0, -515), rotY: 0 },
      { type: 'Z330_2_AUTOBAHN_ENDE', pos: new THREE.Vector3(-4, 0, -515), rotY: Math.PI },
      // South interchange
      { type: 'Z330_1_AUTOBAHN_BEGINN', pos: new THREE.Vector3(-4, 0, 515), rotY: Math.PI },
      { type: 'Z330_2_AUTOBAHN_ENDE', pos: new THREE.Vector3(4, 0, 515), rotY: 0 },
      // East interchange
      { type: 'Z330_1_AUTOBAHN_BEGINN', pos: new THREE.Vector3(515, 0, 4), rotY: Math.PI / 2 },
      { type: 'Z330_2_AUTOBAHN_ENDE', pos: new THREE.Vector3(515, 0, -4), rotY: -Math.PI / 2 },
      // West interchange
      { type: 'Z330_1_AUTOBAHN_BEGINN', pos: new THREE.Vector3(-515, 0, -4), rotY: -Math.PI / 2 },
      { type: 'Z330_2_AUTOBAHN_ENDE', pos: new THREE.Vector3(-515, 0, 4), rotY: Math.PI / 2 },

      // 4. Ortstafeln at town exits (±200m)
      { type: 'Z311_ORTSTAFEL_ENDE', pos: new THREE.Vector3(4, 0, 200), rotY: 0 },
      { type: 'Z282_AUFHEBUNG', pos: new THREE.Vector3(-4, 0, 200), rotY: Math.PI },
      { type: 'Z311_ORTSTAFEL_ENDE', pos: new THREE.Vector3(-4, 0, -200), rotY: Math.PI },
      { type: 'Z282_AUFHEBUNG', pos: new THREE.Vector3(4, 0, -200), rotY: 0 },
      { type: 'Z311_ORTSTAFEL_ENDE', pos: new THREE.Vector3(200, 0, 4), rotY: Math.PI / 2 },
      { type: 'Z282_AUFHEBUNG', pos: new THREE.Vector3(200, 0, -4), rotY: -Math.PI / 2 },
      { type: 'Z311_ORTSTAFEL_ENDE', pos: new THREE.Vector3(-200, 0, -4), rotY: -Math.PI / 2 },
      { type: 'Z282_AUFHEBUNG', pos: new THREE.Vector3(-200, 0, 4), rotY: Math.PI / 2 },

      // 5. Exit countdown baken before off-ramps
      // North off-ramp
      { type: 'Z450_BAKE_300M', pos: new THREE.Vector3(-6, 0, -524), rotY: 0 },
      { type: 'Z450_BAKE_200M', pos: new THREE.Vector3(-6, 0, -549), rotY: 0 },
      { type: 'Z450_BAKE_100M', pos: new THREE.Vector3(-6, 0, -564), rotY: 0 },
      // South off-ramp
      { type: 'Z450_BAKE_300M', pos: new THREE.Vector3(6, 0, 524), rotY: Math.PI },
      { type: 'Z450_BAKE_200M', pos: new THREE.Vector3(6, 0, 549), rotY: Math.PI },
      { type: 'Z450_BAKE_100M', pos: new THREE.Vector3(6, 0, 564), rotY: Math.PI },
      // East off-ramp
      { type: 'Z450_BAKE_300M', pos: new THREE.Vector3(524, 0, -6), rotY: -Math.PI / 2 },
      { type: 'Z450_BAKE_200M', pos: new THREE.Vector3(549, 0, -6), rotY: -Math.PI / 2 },
      { type: 'Z450_BAKE_100M', pos: new THREE.Vector3(564, 0, -6), rotY: -Math.PI / 2 },
      // West off-ramp
      { type: 'Z450_BAKE_300M', pos: new THREE.Vector3(-524, 0, 6), rotY: Math.PI / 2 },
      { type: 'Z450_BAKE_200M', pos: new THREE.Vector3(-549, 0, 6), rotY: Math.PI / 2 },
      { type: 'Z450_BAKE_100M', pos: new THREE.Vector3(-564, 0, 6), rotY: Math.PI / 2 },
    ];

    for (const s of abSigns) {
      const signMesh = TrafficSigns.createSign(s.type, s.pos, s.rotY);
      this.scene.add(signMesh);
    }
  }

  private buildLeitpfosten() {
    const postWhiteMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.6 });
    const postBlackMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.6 });

    const createPost = (pos: THREE.Vector3) => {
      const g = new THREE.Group();
      g.position.copy(pos);

      const whiteGeom = new THREE.CylinderGeometry(0.06, 0.06, 0.7, 8);
      const whiteMesh = new THREE.Mesh(whiteGeom, postWhiteMat);
      whiteMesh.position.y = 0.35;
      whiteMesh.castShadow = true;

      const blackGeom = new THREE.CylinderGeometry(0.06, 0.06, 0.3, 8);
      const blackMesh = new THREE.Mesh(blackGeom, postBlackMat);
      blackMesh.position.y = 0.85;
      blackMesh.castShadow = true;

      g.add(whiteMesh, blackMesh);
      this.scene.add(g);
    };

    // North Landstraße (x=0, z: 210..510)
    for (let z = 210; z <= 510; z += 35) {
      createPost(new THREE.Vector3(-4.5, 0, z));
      createPost(new THREE.Vector3(4.5, 0, z));
    }
    // South Landstraße (x=0, z: -210..-510)
    for (let z = -210; z >= -510; z -= 35) {
      createPost(new THREE.Vector3(-4.5, 0, z));
      createPost(new THREE.Vector3(4.5, 0, z));
    }
    // East Landstraße (z=0, x: 210..510)
    for (let x = 210; x <= 510; x += 35) {
      createPost(new THREE.Vector3(x, 0, -4.5));
      createPost(new THREE.Vector3(x, 0, 4.5));
    }
    // West Landstraße (z=0, x: -210..-510)
    for (let x = -210; x >= -510; x -= 35) {
      createPost(new THREE.Vector3(x, 0, -4.5));
      createPost(new THREE.Vector3(x, 0, 4.5));
    }
  }

  private buildIndustrialZone() {
    const whMat = new THREE.MeshStandardMaterial({
      color: 0x4b5563,
      roughness: 0.9,
      metalness: 0.2,
    });
    const warehouses = [
      { x: 400, y: 4, z: -100, w: 40, h: 8, d: 20 },
      { x: 440, y: 3, z: 0, w: 35, h: 6, d: 20 },
      { x: 410, y: 3.5, z: 100, w: 30, h: 7, d: 15 },
      { x: 470, y: 4, z: -60, w: 45, h: 8, d: 25 },
      { x: 490, y: 3, z: 120, w: 25, h: 6, d: 12 },
    ];
    for (const wh of warehouses) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(wh.w, wh.h, wh.d), whMat);
      mesh.position.set(wh.x, wh.y, wh.z);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);

      const body = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.staticObstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      body.addShape(new CANNON.Box(new CANNON.Vec3(wh.w / 2, wh.h / 2, wh.d / 2)));
      body.position.set(wh.x, wh.y, wh.z);
      this.physicsWorld.world.addBody(body);
    }

    // Factory chimney
    const chimneyMat = new THREE.MeshStandardMaterial({ color: 0x78350f, roughness: 0.85 });
    const chimney = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 2.0, 30, 16), chimneyMat);
    chimney.position.set(510, 15, -150);
    chimney.castShadow = true;
    this.scene.add(chimney);

    const cBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      material: this.physicsWorld.staticObstacleMaterial,
      collisionFilterGroup: COLLISION_GROUP_STATIC,
      collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
    });
    cBody.addShape(new CANNON.Cylinder(1.5, 2.0, 30, 16));
    cBody.position.set(510, 15, -150);
    this.physicsWorld.world.addBody(cBody);

    // Loading bay ground markings
    const bayMat = new THREE.MeshBasicMaterial({ color: 0xeab308 });
    const bayGeom = new THREE.PlaneGeometry(6, 10);
    const bayPositions = [
      new THREE.Vector3(400, 0.01, -160),
      new THREE.Vector3(430, 0.01, -160),
      new THREE.Vector3(460, 0.01, -160),
    ];
    for (const bp of bayPositions) {
      const bay = new THREE.Mesh(bayGeom, bayMat);
      bay.rotation.x = -Math.PI / 2;
      bay.position.copy(bp);
      this.scene.add(bay);
    }

    // 2 Parked trucks
    const truckMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, roughness: 0.4 });
    const truckPositions = [
      new THREE.Vector3(402, 1.25, -155),
      new THREE.Vector3(432, 1.25, -155),
    ];
    for (const tp of truckPositions) {
      const truck = new THREE.Mesh(new THREE.BoxGeometry(2.5, 2.5, 9.0), truckMat);
      truck.position.copy(tp);
      truck.castShadow = true;
      this.scene.add(truck);

      const tBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        material: this.physicsWorld.obstacleMaterial,
        collisionFilterGroup: COLLISION_GROUP_STATIC,
        collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
      });
      tBody.addShape(new CANNON.Box(new CANNON.Vec3(1.25, 1.25, 4.5)));
      tBody.position.set(tp.x, tp.y, tp.z);
      this.physicsWorld.world.addBody(tBody);
    }

    // Industrial access road (Tempo 30)
    this.roadNetwork.createStraightRoad('ind_access', new THREE.Vector3(120, 0, 0), new THREE.Vector3(380, 0, 0), 8.0, 30, 'dashed', false, true, false, false, false);
  }

  private addRoadsideParkedCar(pos: THREE.Vector3, rotY: number, colorHex: number) {
    const carMat = new THREE.MeshStandardMaterial({ color: colorHex, metalness: 0.7, roughness: 0.3 });
    const carMesh = new THREE.Group();

    const body = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.6, 4.2), carMat);
    body.position.y = 0.45;
    body.castShadow = true;

    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 2.3), carMat);
    cabin.position.set(0, 0.95, -0.2);
    cabin.castShadow = true;

    // 4 wheels
    const wheelMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
    const wheelGeom = new THREE.CylinderGeometry(0.3, 0.3, 0.2, 12);
    const offsets = [
      [-0.85, 0.3, 1.4],
      [0.85, 0.3, 1.4],
      [-0.85, 0.3, -1.4],
      [0.85, 0.3, -1.4],
    ];
    for (const [wx, wy, wz] of offsets) {
      const wheel = new THREE.Mesh(wheelGeom, wheelMat);
      wheel.position.set(wx, wy, wz);
      wheel.rotation.z = Math.PI / 2;
      carMesh.add(wheel);
    }

    carMesh.add(body, cabin);
    carMesh.position.copy(pos);
    carMesh.rotation.y = rotY;
    this.scene.add(carMesh);

    // Physics body
    const physBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      material: this.physicsWorld.obstacleMaterial,
      collisionFilterGroup: COLLISION_GROUP_STATIC,
      collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
    });
    physBody.addShape(new CANNON.Box(new CANNON.Vec3(0.9, 0.6, 2.1)));
    physBody.position.set(pos.x, 0.6, pos.z);
    physBody.quaternion.setFromEuler(0, rotY, 0);
    this.physicsWorld.world.addBody(physBody);

    // Register position for RuleEvaluators
    this.parkedCarPositions.push(pos.clone());
  }

  private buildRoadsideParkedCars() {
    const cars = [
      // Street 1: North residential street at z ≈ 60 (parallel to street in right-hand travel direction)
      { pos: new THREE.Vector3(-80, 0, 63.5), rotY: Math.PI / 2, color: 0x2563eb },
      { pos: new THREE.Vector3(-65, 0, 56.5), rotY: -Math.PI / 2, color: 0xf8fafc },
      { pos: new THREE.Vector3(-48, 0, 63.5), rotY: Math.PI / 2, color: 0x94a3b8 },
      { pos: new THREE.Vector3(-30, 0, 56.5), rotY: -Math.PI / 2, color: 0xdc2626 },

      // Street 2: South residential street at z ≈ -60 (parallel to street in right-hand travel direction)
      { pos: new THREE.Vector3(-80, 0, -56.5), rotY: Math.PI / 2, color: 0x64748b },
      { pos: new THREE.Vector3(-65, 0, -63.5), rotY: -Math.PI / 2, color: 0x1e3a5f },
      { pos: new THREE.Vector3(-48, 0, -56.5), rotY: Math.PI / 2, color: 0x6b7280 },
      { pos: new THREE.Vector3(-30, 0, -63.5), rotY: -Math.PI / 2, color: 0xfef9c3 },

      // Street 3: West residential cross street at x ≈ -80 (parallel to N-S street in right-hand direction)
      { pos: new THREE.Vector3(-76.5, 0, -50), rotY: Math.PI, color: 0x16a34a },
      { pos: new THREE.Vector3(-83.5, 0, -30), rotY: 0, color: 0x7c3aed },
      { pos: new THREE.Vector3(-76.5, 0, 10), rotY: Math.PI, color: 0xf97316 },
      { pos: new THREE.Vector3(-83.5, 0, 40), rotY: 0, color: 0x0f172a },
    ];

    for (const c of cars) {
      this.addRoadsideParkedCar(c.pos, c.rotY, c.color);
    }
  }

  public update(deltaTime: number) {
    for (const tl of this.trafficLights) {
      tl.update(deltaTime);
    }

    // Synchronize dynamic knockdown props (signs, bollards) with Cannon-ES physics & gravity
    for (const prop of this.dynamicProps) {
      // Only update transform if the body is dynamic (was knocked down)
      if (prop.body.type === CANNON.Body.DYNAMIC) {
        prop.mesh.position.set(prop.body.position.x, prop.body.position.y, prop.body.position.z);
        prop.mesh.quaternion.set(
          prop.body.quaternion.x,
          prop.body.quaternion.y,
          prop.body.quaternion.z,
          prop.body.quaternion.w
        );
      }
    }
  }

}

